import 'whatwg-fetch';
import * as moment from 'moment';
import retry from './retry';
import errors from './errors';
import crypto from '../crypto';
import { notification } from 'antd';
import { ClipboardFn } from 'common/components/Clipboard';
import * as intl from 'react-intl-universal';
import * as React from 'react';
import { triggerMfaVerify } from '../mfa';
import { sentryReport } from '../../../../lib/sentry';
import { ErrorCode } from '../../../../types/errors';

export { errors };

// 请求方法类型
type Method = 'get' | 'post' | 'upload';

/**
 * 获取GET请求缓存时间戳
 */
const getTimestamp = () => moment().format('YYYYMMDDHHmm');

/**
 * 获取发起请求参数
 * @param method
 * @param url
 * @param data
 */
const getRequestParams: any = (method: Method, url: string, param?: any) => {
    let data = Object.entries(param || {})
        .filter(([, value]) => (method === 'get' ? typeof value === 'number' || !!value : true))
        .reduce((prev: any, [key, value]: [string, any]) => ({ ...prev, [key]: value }), {});
    data = crypto.paramEncrypt(data, url, method);
    // 设置TraceID
    const headersTraceId = {};
    switch (method) {
        case 'get': {
            return {
                info: encodeURI(
                    `${url}?${Object.entries(data || {})
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : value}`)
                        .reduce((prev: any, curr: any) => `${prev}&${curr}`, `t=${getTimestamp()}`)}`,
                ),
                init: {
                    credentials: 'same-origin',
                    headers: headersTraceId,
                },
            };
        }
        case 'post': {
            return {
                info: encodeURI(url),
                init: {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        ...headersTraceId,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify(data),
                },
            };
        }
        case 'upload': {
            return {
                info: encodeURI(url),
                init: {
                    credentials: 'include',
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        ...headersTraceId,
                    },
                    body: Object.entries(data).reduce((formData: any, [key, value]) => {
                        formData.append(key, value);
                        return formData;
                    }, new FormData()),
                },
            };
        }
    }
};

const decryptHandler = (url: string, method: string) => (res: any | { ciphertext: string }) =>
    crypto.resDecrypt(res, url, method);

/**
 * HTTP返回状态码处理
 * @param res 响应数据
 */
const statusHandler = (res: any) => (res.ok || res.status === 429 ? res.json() : { code: res.status });

/**
 * 接口返回状态码处理
 * @param res 响应数据
 */
const codeHandler = async (res: any) => {
    const { code, data } = res;
    if (code === ErrorCode.MfaRequired) {
        try {
            await triggerMfaVerify();
        } catch {
            // eslint-disable-next-line @typescript-eslint/no-throw-literal
            throw { _mfaCancelled: true };
        }
        throw { ...res, _mfaRetry: true };
    }
    if (code) {
        throw res;
    } else {
        return code === 0 ? data : res;
    }
};

// 刷新加密接口配置
const refashEncryptRoutes = async () => {
    const { info, init } = getRequestParams('get', '/v2/control/encryptRoutes');
    let response: Response;

    await fetch(info, init)
        .then(async (res) => {
            // eslint-disable-next-line @typescript-eslint/await-thenable
            response = await res.clone();
            return res;
        })
        .then(statusHandler)
        .then((res) => {
            const { code, data } = res;
            if (code === 0) crypto.refashRoutes(data);
        })
        .catch((err) => {
            init.url = info;
            void sentryReport(init, err, response);
        });
};

interface Config {
    onError: (res: any, url: string) => any;
    retry: {
        list: string[];
        callback?: (delay: number) => any;
    };
}

interface Options {
    ignoreError?: boolean;
    /**
     * 取消信号：用于打断正在进行的请求（如列表页发起新一轮检索时）
     * Abort signal: lets a caller cancel an in-flight request (e.g. when a list page
     * starts a new round of search).
     */
    signal?: AbortSignal;
}

/**
 * 判断错误是否为「主动打断请求」产生的
 * Detects the error thrown when a request is aborted on purpose.
 */
const isAbortError = (err: any): boolean => err?.name === 'AbortError' || err?.code === 20;

(window as any).traceIdList = [];
const renderNotification = (copy: (text: string) => void, traceId: string) => {
    const traceIdList = (window as any).traceIdList;
    if (traceIdList.length >= 3) {
        notification.close(traceIdList.shift());
    }
    notification.error({
        key: traceId,
        top: 55,
        message: intl.get('Global.NotificationWarningTitle'),
        duration: null,
        description: React.createElement(
            'ul',
            { className: 'Warning-notification' },
            React.createElement(
                'a',
                {
                    className: 'id',
                    onClick: () => {
                        copy(traceId);
                    },
                },
                intl.get('Global.NotificationWarningId', { field: traceId }),
            ),
            React.createElement('li', { className: 'desc' }, intl.get('Global.NotificationWarningDescription')),
        ),
        onClose: () => {
            const i = traceIdList.indexOf(traceId);
            traceIdList.splice(i, 1);
        },
    });
    traceIdList.push(traceId);
};
const handleRequestError = async ({
    err,
    init,
    info,
    response,
    traceId,
    options,
    config,
    url,
}: {
    err: any;
    init: any;
    info: string;
    response: Response;
    traceId: string;
    options: Options;
    config: Config;
    url: string;
}) => {
    init.url = info;
    void sentryReport(init, err, response);
    if (err && Number(err.code) === 1007) {
        await refashEncryptRoutes();
    } else {
        const code = err.code?.toString?.();
        const filterCodes = Object.keys(errors).filter((key: string) =>
            ['1001', '1002', '1003', '1004', '1005', '1006'].includes(key),
        );
        if (err.traceId) traceId = err.traceId;
        if (traceId && !options.ignoreError && filterCodes.includes(code))
            ClipboardFn()((copy: (text: string) => void) => {
                renderNotification(copy, traceId);
            });
    }
    if (err.isMock) return err.data;
    !options.ignoreError && config.onError(err, url);
    throw err;
};

export default (config: Config) =>
    (method: Method) =>
    async (url: string, data?: any, options: Options = { ignoreError: false }) => {
        const { info, init } = getRequestParams(method, url, data, options);
        let traceId = '';
        let response: any;

        const doFetch = async () =>
            await fetch(info, { ...init, signal: options.signal })
                .then(async (res) => {
                    traceId = res.headers.get('x-trace-id') || '';
                    // eslint-disable-next-line @typescript-eslint/await-thenable
                    response = await res.clone();
                    return res;
                })
                .then(statusHandler)
                .then(decryptHandler(url, method))
                .then(codeHandler);

        try {
            return await retry(config.retry)(doFetch, url);
        } catch (err) {
            if (err._mfaCancelled) {
                throw err;
            }
            // 请求被主动打断：不是失败，不上报 Sentry、不弹错误提示
            // Request aborted on purpose: not a failure, so skip the Sentry report and the error toast.
            if (isAbortError(err)) {
                throw err;
            }
            if (err._mfaRetry) {
                try {
                    return await doFetch();
                } catch (error) {
                    if (isAbortError(error)) {
                        throw error;
                    }
                    return await handleRequestError({
                        err: error,
                        init,
                        info,
                        response,
                        traceId,
                        options,
                        config,
                        url,
                    });
                }
            }
            return await handleRequestError({ err, init, info, response, traceId, options, config, url });
        }
    };
